import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { customGenericStyles } from '@rsos/base-components/Select/Select.styles';
import { ErrorWrapper } from '@rsos/base-components/form/ErrorsWrapper';
import { AIR_AMBULANCE_STANDBY } from '@rsos/capstone/src/constants/capabilities';
import { Box, Text, Select, Button } from '@rsos/components/capstone/base';
import { useFlag } from '@rsos/flags';
import useHasCapability from '@rsos/hooks/useHasCapability';
import { selectExistingRequest } from '@rsos/reduxDataStore';
import { Label, ButtonContainer } from '../AirAmbulanceFormStandalone.styles';
import {
  REQUEST_REASONS,
  FORM_FIELDS,
  DEFAULT_BUTTON_OPTIONS,
  REQUEST_REASON,
  LAUNCH_TYPES,
} from '../constants';
import { REQUIRED_ERROR } from '../validation';
import { RadioButtonTitle } from './RequestSection.styles';

const StyledButton = styled(Button)`
  ${({ theme, $isTest, $isSelected, btnType }) =>
    $isTest && $isSelected && btnType === 'primary'
      ? `
          background-color: ${theme.colors.caution};
          &:hover {
            filter: brightness(0.8);
            background-color: ${theme.colors.caution};
          }
        `
      : ''}
`;

const RequestSection = ({
  values,
  touched,
  setFieldValue,
  setFieldTouched,
}) => {
  const hasGmrStandbyCapability = useHasCapability(
    AIR_AMBULANCE_STANDBY,
  )?.hasCapability;
  const existingRequest = useSelector(selectExistingRequest);
  const hasError = useMemo(() => {
    return touched.request_reason && values.request_reason === 'Select';
  }, [touched, values]);
  const styles = useMemo(() => {
    return {
      ...customGenericStyles,
      control: (base, state) => ({
        ...customGenericStyles.control(base, state),
        borderColor: hasError
          ? state.selectProps.theme.colors.lightDanger
          : state.selectProps.theme.colors.heavyLineOutline,
        opacity: state.isDisabled ? 0.5 : 1,
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        pointerEvents: 'auto',
      }),
    };
  }, [hasError]);
  return (
    <>
      <Box pb={3}>
        <Label>
          <Text size="small" color="primaryText">
            Request Reason*
          </Text>
        </Label>
        <Select
          name="request_reason"
          options={REQUEST_REASONS}
          placeholder="Please select"
          isClearable={false}
          isSearchable={false}
          onChange={selectedOption => {
            setFieldTouched(REQUEST_REASON, true);
            setFieldValue(REQUEST_REASON, selectedOption);
          }}
          onBlur={() => setFieldTouched(REQUEST_REASON, true)}
          value={values.request_reason}
          styles={styles}
          isDisabled={existingRequest}
        />
        {hasError && (
          <ErrorWrapper error={REQUIRED_ERROR} id={'request_reason_error'} />
        )}
      </Box>

      {FORM_FIELDS.map(({ label, field, options = DEFAULT_BUTTON_OPTIONS }) => {
        const filteredOptions =
          field === 'launch_type' && !hasGmrStandbyCapability
            ? options.filter(option => option !== LAUNCH_TYPES.STANDBY)
            : options;
        return (
          <Box pb={4} key={field}>
            <Label>
              <RadioButtonTitle hasError={!values[field]} size="small">
                {label}
              </RadioButtonTitle>
            </Label>
            <ButtonContainer horizontal>
              {filteredOptions.map(option => (
                <StyledButton
                  key={option}
                  type="button"
                  btnType={values[field] === option ? 'primary' : 'tertiary'}
                  onClick={() => setFieldValue(field, option)}
                  grow
                  disabled={existingRequest}
                  $isTest={
                    field === 'launch_type' && option === LAUNCH_TYPES.TEST
                  }
                  $isSelected={values[field] === option}
                >
                  {option}
                </StyledButton>
              ))}
            </ButtonContainer>
          </Box>
        );
      })}
    </>
  );
};

export default RequestSection;
